/**
 * WysiwygEditor - Editor WYSIWYG completo usando TipTap
 * Suporta formatacao rica: negrito, italico, sublinhado, cores, alinhamento, imagens, links
 * Utilizado em Templates de Propostas Comerciais
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import { useEffect, useCallback } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, AlignLeft, AlignCenter,
  AlignRight, AlignJustify, Link2, Image as ImageIcon, List, ListOrdered,
  Heading1, Heading2, Minus, Quote, Highlighter, Undo, Redo,
} from "lucide-react";

const TOOLBAR_COLORS = [
  "#0f172a", "#1e3a8a", "#1d4ed8", "#0369a1", "#065f46",
  "#7c3aed", "#be185d", "#b91c1c", "#92400e", "#374151",
];

function ToolBtn({ onClick, active, title, children, disabled }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors text-[13px] ${active ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

function ToolSep() {
  return <span className="w-px h-5 bg-slate-200 mx-0.5 self-center" />;
}

export default function WysiwygEditor({ value, onChange, placeholder = "Escreve aqui o conteudo...", minHeight = 280, variables = [] }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-2",
        style: `min-height:${minHeight}px`,
        "data-placeholder": placeholder,
      },
    },
  });

  // Sync external value change (ex: ao carregar template)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "", false);
  }, [value]); // eslint-disable-line

  const insertVariable = useCallback((v) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${v}}}`).run();
  }, [editor]);

  const addImage = useCallback(() => {
    const url = window.prompt("URL da imagem:");
    if (url) editor?.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const addLink = useCallback(() => {
    const url = window.prompt("URL do link:");
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Barra de ferramentas */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
        {/* Historico */}
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Desfazer"><Undo size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Refazer"><Redo size={14} /></ToolBtn>
        <ToolSep />
        {/* Headings */}
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Titulo 1"><Heading1 size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titulo 2"><Heading2 size={14} /></ToolBtn>
        <ToolSep />
        {/* Formatacao basica */}
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrito"><Bold size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italico"><Italic size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Sublinhado"><UnderlineIcon size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Riscado"><Strikethrough size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive("highlight")} title="Destaque"><Highlighter size={14} /></ToolBtn>
        <ToolSep />
        {/* Alinhamento */}
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Esquerda"><AlignLeft size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centro"><AlignCenter size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Direita"><AlignRight size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justificado"><AlignJustify size={14} /></ToolBtn>
        <ToolSep />
        {/* Listas */}
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista"><List size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada"><ListOrdered size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citacao"><Quote size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linha divisoria"><Minus size={14} /></ToolBtn>
        <ToolSep />
        {/* Media */}
        <ToolBtn onClick={addLink} active={editor.isActive("link")} title="Inserir link"><Link2 size={14} /></ToolBtn>
        <ToolBtn onClick={addImage} title="Inserir imagem por URL"><ImageIcon size={14} /></ToolBtn>
        <ToolSep />
        {/* Cores */}
        {TOOLBAR_COLORS.map((c) => (
          <button
            key={c} type="button"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setColor(c).run(); }}
            title={c}
            className="w-4 h-4 rounded-full border border-white shadow-sm hover:scale-110 transition-transform"
            style={{ background: c }}
          />
        ))}
        {/* Variaveis */}
        {variables.length > 0 && (
          <>
            <ToolSep />
            <select
              className="text-[11px] border border-slate-200 rounded px-1.5 py-1 text-slate-600 bg-white"
              onChange={(e) => { if (e.target.value) { insertVariable(e.target.value); e.target.value = ""; } }}
              defaultValue=""
              title="Inserir variavel"
            >
              <option value="">+ Variavel</option>
              {variables.map((v) => <option key={v} value={v}>{`{{${v}}}`}</option>)}
            </select>
          </>
        )}
      </div>
      {/* Area de edicao */}
      <EditorContent editor={editor} className="wysiwyg-content" />
      <style>{`
        .wysiwyg-content .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .wysiwyg-content .ProseMirror { outline: none; }
        .wysiwyg-content .prose h1 { font-size: 1.5rem; font-weight: 700; margin: 0.75rem 0 0.5rem; }
        .wysiwyg-content .prose h2 { font-size: 1.2rem; font-weight: 700; margin: 0.6rem 0 0.4rem; }
        .wysiwyg-content .prose blockquote { border-left: 3px solid #1e3a8a; padding-left: 0.75rem; color: #475569; }
        .wysiwyg-content .prose hr { border-color: #e2e8f0; margin: 0.75rem 0; }
      `}</style>
    </div>
  );
}
